import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { consoleApi, type ApproveBody } from '@/lib/consoleApi';
import { canApprove } from '@/lib/moderationDecision';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AdminPage } from '@/admin';

const CHECKS = [
  'No prompt-injection / instructions to the model in the text',
  'Content matches the claimed source + title',
  'Metadata (domain, source type, date) is correct',
];

const META_FIELDS: { key: keyof Omit<ApproveBody, 'checklist_completed'>; label: string; placeholder: string }[] = [
  { key: 'title', label: 'Title', placeholder: 'Document title' },
  { key: 'source', label: 'Source', placeholder: 'e.g. Cochrane, PubMed' },
  { key: 'domain_tag', label: 'Domain', placeholder: 'e.g. cardiovascular' },
  { key: 'source_type', label: 'Source type', placeholder: 'e.g. meta_analysis' },
  { key: 'document_date', label: 'Document date', placeholder: 'YYYY-MM-DD' },
];

export function ModerationDocumentPage() {
  const { documentId = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const doc = useQuery({ queryKey: ['moderation-doc', documentId], queryFn: () => consoleApi.moderation.document(documentId) });

  const [ticks, setTicks] = useState<boolean[]>(CHECKS.map(() => false));
  const [meta, setMeta] = useState<Omit<ApproveBody, 'checklist_completed'>>({
    domain_tag: '', source_type: '', document_date: '', title: '', source: '',
  });
  const [reason, setReason] = useState('');

  const onDone = () => { void qc.invalidateQueries({ queryKey: ['moderation-queue'] }); navigate('/moderation', { replace: true }); };
  const approve = useMutation({
    mutationFn: () => consoleApi.moderation.approve(documentId, { ...meta, checklist_completed: canApprove(ticks) }),
    onSuccess: onDone,
  });
  const reject = useMutation({ mutationFn: () => consoleApi.moderation.reject(documentId, { reason }), onSuccess: onDone });

  return (
    <AdminPage
      title="Review document"
      description="Check the document for prompt-injection and metadata accuracy before it enters the Research Database."
      backTo={{ to: '/moderation', label: 'Moderation queue' }}
    >
      <div className="mb-13 font-mono text-[12px] text-text-muted">{documentId}</div>
      {doc.isLoading ? <p className="text-body-sm text-text-muted">Loading…</p> : null}
      {doc.isError ? <p className="text-body-sm text-flagged">Could not load the document.</p> : null}

      {doc.data ? (
        <div className="space-y-21">
          <section className="rounded border border-border bg-surface p-21">
            <h2 className="mb-8 text-h3 font-semibold">Plaintext</h2>
            <pre className="max-h-80 overflow-auto rounded border border-border bg-surface-sunken p-13 text-[12px] whitespace-pre-wrap">{doc.data.content}</pre>
          </section>

          <section className="rounded border border-border bg-surface p-21">
            <h2 className="mb-8 text-h3 font-semibold">Injection checklist</h2>
            <div className="space-y-2">
              {CHECKS.map((c, i) => (
                <label key={i} className="flex items-start gap-2 text-body-sm">
                  <input type="checkbox" className="mt-0.5" checked={ticks[i]} onChange={(e) => setTicks((t) => t.map((v, j) => (j === i ? e.target.checked : v)))} />
                  {c}
                </label>
              ))}
            </div>
          </section>

          <section className="rounded border border-border bg-surface p-21">
            <h2 className="mb-8 text-h3 font-semibold">Metadata</h2>
            <div className="grid grid-cols-2 gap-13">
              {META_FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="mb-1 block text-body-sm font-medium">{f.label}</label>
                  <Input value={meta[f.key]} onChange={(e) => setMeta((m) => ({ ...m, [f.key]: e.target.value }))} placeholder={f.placeholder} />
                </div>
              ))}
            </div>
          </section>

          <div className="flex flex-wrap items-center gap-8">
            <Button data-testid="approve-btn" size="sm" disabled={!canApprove(ticks) || approve.isPending} onClick={() => approve.mutate()}>
              {approve.isPending ? 'Approving…' : 'Approve → ingest'}</Button>
            <div className="flex items-center gap-2">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reject reason" className="w-[220px]" />
              <Button data-testid="reject-btn" variant="destructive" size="sm" disabled={reason.trim().length === 0 || reject.isPending} onClick={() => reject.mutate()}>
                {reject.isPending ? 'Rejecting…' : 'Reject'}</Button>
            </div>
          </div>
          {(approve.isError || reject.isError) ? <p className="text-body-sm text-flagged">Action failed.</p> : null}
        </div>
      ) : null}
    </AdminPage>
  );
}
